import { SupplyPreviewPlot } from "components/autoRecipeParameters/SupplyPreviewPlot";
import { AutoRecipeDefaultsLoadError } from "components/autoRecipeParameters/AutoRecipeDefaultsLoadError";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useNavigateToNewAutoSearch } from "hooks/navigation";
import {
  AutoRecipeDefaultsRead,
  SearchType,
  useGetAutoRecipeDefaultsQuery,
} from "store/api/generatedApi";
import { AutoRecipeParametersForm } from "components/autoRecipeParameters/AutoRecipeParametersForm";
import { useAutoRecipeEditor } from "components/autoRecipeParameters/useAutoRecipeEditor";
import { AutoRecipeEditorAlerts } from "components/autoRecipeParameters/AutoRecipeEditorAlerts";
import { searchTypeLabels } from "components/autoRecipeParameters/searchTypeLabels";
import { PendingParameterChecks } from "components/defaultParameters/PendingParameterChecks";

import { SearchEffectiveTimestamp } from "./SearchEffectiveTimestamp";

const LoadedAutoRecipeDialog = ({
  defaults,
  refresh,
  close,
}: {
  defaults: AutoRecipeDefaultsRead;
  refresh: () => Promise<unknown>;
  close: () => void;
}) => {
  const { t } = useTenantTranslation();
  const editor = useAutoRecipeEditor({ defaults, scope: "search" });
  const { source, draft, setDraft, isStale, isValid, isChecking } = editor;
  const [error, setError] = useState(false);
  const [timestamp, setTimestamp] = useState<string | null>(() =>
    dayjs().toISOString()
  );
  const [create, { isLoading }] = useNavigateToNewAutoSearch();
  const hasInvalidParameters = !isValid || timestamp === null;
  return (
    <Dialog
      open
      fullWidth
      maxWidth="lg"
      onClose={() => {
        if (!isLoading) close();
      }}
    >
      <DialogTitle>
        {t(searchTypeLabels[source.context.search_type])}
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={3}>
          <AutoRecipeEditorAlerts
            editor={editor}
            refresh={refresh}
            disabled={isLoading}
          />
          {error ? (
            <Alert severity="error">{t("autoCreateFailed")}</Alert>
          ) : null}
          <Typography variant="body2">
            {t("autoSearchOnlyParameters")}
          </Typography>
          <SearchEffectiveTimestamp
            timestamp={timestamp}
            onChange={setTimestamp}
            disabled={isLoading}
          />
          <AutoRecipeParametersForm
            supplyPreview={
              <SupplyPreviewPlot
                key={timestamp}
                context={source.context}
                parameters={draft}
                scope="search"
                referenceTimestamp={timestamp}
                isValid={editor.schemaValidation.valid}
              />
            }
            scope="search"
            validation={editor.schemaValidation}
            parameters={draft}
            definition={source.definition}
            onChange={setDraft}
            disabled={isLoading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        {isChecking ? <PendingParameterChecks /> : null}
        <Button onClick={close} disabled={isLoading}>
          {t("cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isLoading}
          disabled={hasInvalidParameters || isStale}
          onClick={() => {
            setError(false);
            void create({
              auto_recipe_type: source.context.search_type,
              on_timestamp: timestamp ?? undefined,
              parameters: draft,
              default_context: source.context,
            })
              .then(close)
              .catch(() => {
                setError(true);
                void refresh();
              });
          }}
        >
          {t("create")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export const AutoRecipeParametersDialog = ({
  searchType,
  close,
}: {
  searchType: SearchType;
  close: () => void;
}) => {
  const tenantName = useTenant();
  const { t } = useTenantTranslation();
  const defaults = useGetAutoRecipeDefaultsQuery(
    { tenantName, searchType, scope: "search" },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );
  if (defaults.currentData)
    return (
      <LoadedAutoRecipeDialog
        key={`${tenantName}/${searchType}`}
        defaults={defaults.currentData}
        refresh={defaults.refetch}
        close={close}
      />
    );
  return (
    <Dialog open fullWidth maxWidth="sm" onClose={close}>
      <DialogTitle>{t(searchTypeLabels[searchType])}</DialogTitle>
      <DialogContent>
        {defaults.isError ? (
          <AutoRecipeDefaultsLoadError
            error={defaults.error}
            searchType={searchType}
            refresh={defaults.refetch}
          />
        ) : (
          <CircularProgress />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>{t("cancel")}</Button>
      </DialogActions>
    </Dialog>
  );
};
